import {get_criteria_for_analysis} from "../Services/critere.service";
import {get_all_templates} from "../Services/template.service";
import {getAnalyseFull} from "../Services/review.service";
import {
    loadVersionContent,
    setGlobalLoading, setStreamingIa,
} from "./documents.action";
import {
    finalizeAfterStream,
    flushAnalyseVersion, generate_summary_from_comments, getIAReviewResults,
    launchAnalysisStream
} from "../Services/stage.service";
import { reportTechnicalAutoFeedback } from "../Services/feedback.service";
import {DocumentActionTypes, IAStreamActionTypes, StageActionTypes} from "./actionTypes";
import {setCriterionError} from "./critere.action";
import i18n from "../i18n";
import Swal from "sweetalert2";
import { finishAiTask, startAiTask } from "../Reducers/aiTasksReducer";
import {
    adaptFastGlobalCommentCard,
    COMPARISON_EVENT_SCHEMA,
    FAST_GLOBAL_EVENT_SCHEMA,
    FAST_GLOBAL_BATCH_EVENT_SCHEMA,
    isComparisonStreamEvent,
    isFastGlobalStreamEvent,
    isReferenceReviewStreamEvent,
    readAnalysisTerminal,
} from "../utils/fastGlobalStream";
import {
    clearActiveReservationId,
    fetchCommercialUsage,
    setActiveReservationId,
} from "./commercialControl.action";
import {getAnalysisPov, isSamePov} from "../utils/pov";
import { getGlobalCardPriority, isDirectGlobalCard } from '../utils/globalCardPriority';

let pendingRequests = {};
let analysisStreamInstanceSequence = 0;
const createAnalysisStreamInstanceId = () => (
    `analysis-stream:${Date.now()}:${++analysisStreamInstanceSequence}`
);
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const isUuid = (value) => UUID_RE.test(String(value || ""));
const isComparisonEventType = (eventName) => typeof eventName === "string"
    && eventName.startsWith("comparison_");
const hasCommercialDecisionShape = (value) => {
    if (!value || typeof value !== "object") return false;
    const candidate = value.data && typeof value.data === "object"
        ? value.data
        : value.detail && typeof value.detail === "object"
            ? value.detail
            : value;

    return (
        candidate.allowed === false &&
        ["blocked", "requires_confirmation"].includes(candidate.status) &&
        typeof candidate.code === "string" &&
        candidate.code.length > 0
    );
};

const shouldSurfaceCommercialLaunchFailure = (error) => {
    return (
        hasCommercialDecisionShape(error?.response?.data) ||
        hasCommercialDecisionShape(error)
    );
};

const refreshCommercialUsageBestEffort = (dispatch) => {
    try {
        const result = dispatch(fetchCommercialUsage());
        if (result?.catch) {
            result.catch(() => {});
        }
    } catch (_) {
        // Usage refresh must never mask stream/finalize behavior.
    }
};

const aiTask = {
    analysis: (analyseId, versionId) => ({
        id: `analysis:${analyseId}:${versionId}`,
        kind: "analysis_stream",
        label: i18n.t("review:stage.aiTasks.analysis"),
        scopeId: analyseId,
        blocking: true,
    }),
};

export const fetchStageData = (id) => async (dispatch, getState) => {
    if (pendingRequests[id]) return;
    pendingRequests[id] = true;

    try {
        dispatch(setGlobalLoading(true));

        const analyse = await getAnalyseFull(id);
        const analyseData = analyse.data;

        const currentVersionId = analyseData.versions?.[0]?.id;
        if (!currentVersionId) {
            throw new Error(i18n.t("review:stage.loadError"));
        }

        const principalDoc = analyseData.documents?.find(doc => doc.type === "PRINCIPAL") || {};
        const amendments = analyseData.documents?.filter(doc => doc.type !== "PRINCIPAL") || [];
        const expectedCommentsPov = getAnalysisPov(principalDoc);

        // Publish the contract shell as soon as /full resolves. The document, criteria,
        // templates and POV-scoped comments can then hydrate independently in parallel.
        dispatch({
            type: StageActionTypes.FETCH_STAGE_DATA,
            payload: {
                criteria: [],
                templates: [],
                analyse: {
                    ...analyseData,
                    main_document: principalDoc,
                    amendments: amendments,
                    versions: analyseData.versions || []
                }
            }
        });

        const versionPromise = dispatch(loadVersionContent(currentVersionId, {
            refreshAnalysisData: false,
        }));

        const criteriaPromise = get_criteria_for_analysis(id, currentVersionId)
            .then((criteriaRes) => dispatch({
                type: StageActionTypes.SET_CRITERIA_STAGE,
                payload: criteriaRes.data,
            }))
            .catch(async (criteriaError) => {
                console.warn("Impossible de charger les critères de l'analyse", criteriaError);
                await versionPromise;
                return dispatch(fetchCriteriaForAnalysis(id, currentVersionId));
            });

        const templatesPromise = get_all_templates()
            .then((templatesRes) => dispatch({
                type: StageActionTypes.FETCH_TEMPLATES_SUCCESS,
                payload: templatesRes.data,
            }))
            .catch((templatesError) => {
                console.warn("Impossible de charger les modèles de critères", templatesError);
            });

        const commentsPromise = getIAReviewResults(id, currentVersionId)
            .then((iaCommentsRes) => {
                if (isSamePov(
                    getState().stage?.main_document,
                    expectedCommentsPov,
                )) {
                    dispatch(setIAComments(iaCommentsRes.data));
                }
            })
            .catch((commentsError) => {
                console.warn("Impossible de charger les commentaires IA", commentsError);
                if (isSamePov(
                    getState().stage?.main_document,
                    expectedCommentsPov,
                )) {
                    dispatch(setIAComments([]));
                }
            });

        await Promise.all([
            versionPromise,
            criteriaPromise,
            templatesPromise,
            commentsPromise,
        ]);
    } catch (error) {
        console.error("Impossible de charger la page d'analyse", {
            status: error.response?.status,
            detail: error.response?.data?.detail || error.response?.data?.message || error.message,
        });
        dispatch(setStageError(
            error.response?.data?.error ||
            error.response?.data?.message ||
            error.response?.data?.detail ||
            error.message ||
            i18n.t("review:stage.loadError")
        ));
    } finally {
        delete pendingRequests[id];
        dispatch(setGlobalLoading(false));
    }
};

export const launchAnalysisStreamRequest =
    (analyseId, selectedCriteria, id_doc, launchOptions = {}) =>
        async (dispatch, getState) => {
            const referenceReviewStreamInstanceId = createAnalysisStreamInstanceId();
            const streamedComments = [];
            const streamedCommentDrafts = new Map();
            let targetDocumentVersionId = isUuid(id_doc) ? id_doc : null;
            let comparisonReasoningObservationIndex = 0;
            let activeReservationId = null;
            let activeReferenceReviewId = null;
            const pendingReferenceReviewProgress = [];
            const analysisTaskId = aiTask.analysis(analyseId, targetDocumentVersionId || id_doc).id;
            let streamTerminalState = null;
            let isFinalizingStream = false;
            let isStreamClosed = false;
            let streamFinalizePromise = null;
            let streamFailedOnce = false;
            let analysisStreamMode = ['fast_global', 'comparison', 'reference_review'].includes(launchOptions.reviewMode)
                ? launchOptions.reviewMode
                : null;
            let fastGlobalCardsStarted = false;
            const pendingFastGlobalCards = [];
            const transientFastGlobalCardIds = new Set();
            const directStreamedCardIds = new Set();
            let pendingFastGlobalCompletion = null;
            const comparisonPreviewCardIds = new Set();
            const startedProviderSummaryCriteria = new Set();
            let activeBatchId = null;
            let pendingBatchReading = null;
            let batchReadingTimer = null;
            let lastBatchReadingAt = null;
            const clearBatchReading = () => {
                if (batchReadingTimer !== null) clearTimeout(batchReadingTimer);
                batchReadingTimer = null;
                pendingBatchReading = null;
            };
            const publishBatchActivity = (payload) => {
                const scope = getState?.()?.documentVersion?.provider_summary;
                if (isStreamClosed || isFinalizingStream || fastGlobalCardsStarted
                    || (scope?.stream_instance_id && scope.stream_instance_id !== referenceReviewStreamInstanceId)) return;
                dispatch({ type: IAStreamActionTypes.UPDATE_BATCH_ACTIVITY,
                    payload: { ...payload, stream_instance_id: referenceReviewStreamInstanceId } });
            };
            const updateBatchActivity = (payload) => {
                if (payload.event === 'analysis_mechanism_progress' && payload.status === 'item_completed') {
                    pendingBatchReading = payload;
                    const remaining = lastBatchReadingAt === null ? 0 : Math.max(0, 8000 - (Date.now() - lastBatchReadingAt));
                    if (remaining && batchReadingTimer === null) {
                        batchReadingTimer = setTimeout(() => {
                            batchReadingTimer = null;
                            const latest = pendingBatchReading;
                            pendingBatchReading = null;
                            if (latest) { publishBatchActivity(latest); lastBatchReadingAt = Date.now(); }
                        }, remaining);
                    } else if (!remaining) {
                        publishBatchActivity(payload);
                        lastBatchReadingAt = Date.now();
                        pendingBatchReading = null;
                    }
                } else {
                    clearBatchReading();
                    publishBatchActivity(payload);
                }
            };
            const resetProviderSummary = () => dispatch({
                type: IAStreamActionTypes.RESET_PROVIDER_SUMMARY,
                payload: {
                    analysis_id: analyseId,
                    document_version_id: targetDocumentVersionId || id_doc,
                    stream_instance_id: referenceReviewStreamInstanceId,
                },
            });
            const closeProviderSummary = (status) => {
                clearBatchReading();
                dispatch({ type: IAStreamActionTypes.SET_PROVIDER_SUMMARY_STATUS,
                    payload: { status, stream_instance_id: referenceReviewStreamInstanceId } });
            };

            const getReferenceReviewId = (payload = {}) => (
                payload.reference_review_id
                || payload.review_id
                || payload.reference_review_metadata?.reference_review_id
                || payload.reference_review_metadata?.review_id
                || payload.snapshot_metadata?.review_id
                || null
            );

            const withActiveReferenceReviewIdentity = (payload = {}) => {
                const eventReviewId = getReferenceReviewId(payload);
                const reviewId = eventReviewId || activeReferenceReviewId;
                return {
                    ...payload,
                    analysis_id: payload.analysis_id || analyseId,
                    document_version_id: payload.document_version_id
                        || targetDocumentVersionId
                        || id_doc,
                    ...(reviewId ? {
                        review_id: reviewId,
                        reference_review_id: reviewId,
                    } : {}),
                    stream_instance_id: referenceReviewStreamInstanceId,
                };
            };

            const dispatchReferenceReviewProgress = (type, payload) => {
                const eventReviewId = getReferenceReviewId(payload);
                if (!activeReferenceReviewId && eventReviewId) {
                    activeReferenceReviewId = String(eventReviewId);
                }
                if (!activeReferenceReviewId && !eventReviewId) {
                    pendingReferenceReviewProgress.push({type, payload});
                    return;
                }
                dispatch({
                    type,
                    payload: withActiveReferenceReviewIdentity(payload),
                });
            };

            const activateReferenceReviewContext = (payload) => {
                const reviewId = getReferenceReviewId(payload);
                if (reviewId) activeReferenceReviewId = String(reviewId);
                dispatch({
                    type: IAStreamActionTypes.SET_REFERENCE_REVIEW_CONTEXT,
                    payload: withActiveReferenceReviewIdentity(payload),
                });
                if (!activeReferenceReviewId || pendingReferenceReviewProgress.length === 0) return;
                pendingReferenceReviewProgress.splice(0).forEach((pending) => {
                    dispatchReferenceReviewProgress(pending.type, pending.payload);
                });
            };

            const interruptReferenceReview = (reason) => {
                if (analysisStreamMode !== 'reference_review') return;
                pendingReferenceReviewProgress.length = 0;
                dispatch({
                    type: IAStreamActionTypes.INTERRUPT_REFERENCE_REVIEW,
                    payload: {
                        ...withActiveReferenceReviewIdentity({review_mode: 'reference_review'}),
                        reason,
                        unidentified_run: !activeReferenceReviewId,
                    },
                });
            };

            const commitFastGlobalCard = (comment, { persist = true } = {}) => {
                dispatch(setLoadingIAComments(false));
                dispatch({ type: IAStreamActionTypes.ADD_IA_COMMENT, payload: comment });

                if (
                    analysisStreamMode === 'fast_global'
                    && comment.final_for_run === true
                    && comment.snapshot_persisted !== true
                    && comment.id
                ) {
                    transientFastGlobalCardIds.add(String(comment.id));
                    if (comment.source_review_method === 'direct_original_contract'
                        || comment.metadata_json?.source_review_method === 'direct_original_contract') {
                        directStreamedCardIds.add(String(comment.id));
                    }
                }

                if (comment.error || !persist) return;
                const commentId = comment.id ? String(comment.id) : "";
                const previousDraft = commentId
                    ? (streamedCommentDrafts.get(commentId) || {})
                    : {};
                const mergedDraft = { ...previousDraft, ...comment };
                if (commentId) streamedCommentDrafts.set(commentId, mergedDraft);
                if (!comment.skip_save) streamedComments.push(mergedDraft);
            };

            const removeTransientFastGlobalCards = () => {
                // A complete direct-reading risk is already readable. Keep it
                // in this session if the rest of the run fails, without treating
                // it as a saved analysis or enabling paid follow-up actions.
                const rejectedIds = [...transientFastGlobalCardIds]
                    .filter((id) => !directStreamedCardIds.has(id));
                if (rejectedIds.length === 0) return;
                dispatch({
                    type: IAStreamActionTypes.REMOVE_IA_COMMENTS_BY_IDS,
                    payload: rejectedIds,
                });
                rejectedIds.forEach((id) => transientFastGlobalCardIds.delete(id));
            };

            const startFastGlobalCards = () => {
                if (fastGlobalCardsStarted) return;
                clearBatchReading();
                fastGlobalCardsStarted = true;
                dispatch({ type: IAStreamActionTypes.START_FAST_GLOBAL_CARDS });
            };

            const finishFastGlobalReveal = () => {
                if (
                    fastGlobalCardsStarted
                    || (!pendingFastGlobalCards.length && !pendingFastGlobalCompletion)
                ) return;

                if (!pendingFastGlobalCards.length) {
                    const completion = pendingFastGlobalCompletion;
                    pendingFastGlobalCompletion = null;
                    if (completion) completion();
                    return;
                }

                startFastGlobalCards();
                pendingFastGlobalCards.splice(0).forEach(commitFastGlobalCard);

                const completion = pendingFastGlobalCompletion;
                pendingFastGlobalCompletion = null;
                if (completion) completion();
            };

            const stopFastGlobalObservationReveal = () => {
                pendingFastGlobalCards.length = 0;
                pendingFastGlobalCompletion = null;
            };

            const enqueueFastGlobalObservation = (payload, { upsert = false } = {}) => {
                if (fastGlobalCardsStarted) return;
                dispatch({
                    type: upsert
                        ? IAStreamActionTypes.UPSERT_FAST_GLOBAL_OBSERVATION
                        : IAStreamActionTypes.ADD_FAST_GLOBAL_OBSERVATION,
                    payload,
                });
            };

            const clearFastGlobalObservations = () => {
                stopFastGlobalObservationReveal();
                dispatch({ type: IAStreamActionTypes.CLEAR_FAST_GLOBAL_OBSERVATIONS });
            };

            const detectFastGlobalMode = (payload) => {
                const detectedMode = isReferenceReviewStreamEvent(payload)
                    ? 'reference_review'
                    : isComparisonEventType(payload?.event)
                    ? 'comparison'
                    : isFastGlobalStreamEvent(payload)
                        ? 'fast_global'
                        : isComparisonStreamEvent(payload)
                            ? 'comparison'
                            : null;
                if (!detectedMode) return false;
                // A Comparison stream can carry a compatibility-shaped final
                // card. Never let that generic card reclassify the already
                // selected Comparison flow and skip its deterministic feed.
                if (analysisStreamMode === 'comparison' && detectedMode === 'fast_global') {
                    return false;
                }
                analysisStreamMode = detectedMode;
                dispatch({
                    type: IAStreamActionTypes.SET_ANALYSIS_STREAM_MODE,
                    payload: detectedMode,
                });
                return true;
            };

            const acceptFastGlobalEvent = (payload) => {
                if (detectFastGlobalMode(payload)) return true;
                return ['fast_global', 'comparison'].includes(analysisStreamMode);
            };

            const canonicalFastGlobalEvent = (payload) => ({
                ...payload,
                schema_version: payload?.schema_version
                    || payload?.schema
                    || (analysisStreamMode === 'comparison'
                        ? COMPARISON_EVENT_SCHEMA
                        : FAST_GLOBAL_EVENT_SCHEMA),
            });

            const normalizeComparisonAsObservation = (payload) => {
                const source = payload?.data && typeof payload.data === "object"
                    ? payload.data
                    : payload || {};
                const candidateIndex = source.candidate_index ?? null;
                const candidateId = source.candidate_id || "";
                const fallbackId = source.observation_id || (
                    candidateId
                        ? `comparison-candidate-${candidateId}`
                        : candidateIndex !== null
                            ? `comparison-candidate-${candidateIndex}`
                            : "comparison-progress"
                );
                return canonicalFastGlobalEvent({
                    ...source,
                    event: "analysis_observation",
                    review_mode: source.review_mode || "comparison",
                    provisional: source.provisional !== undefined && source.provisional !== null
                        ? source.provisional
                        : true,
                    observation_id: fallbackId,
                    stage: source.stage || "validation",
                    kind: source.kind || (payload?.event === "comparison_candidate_detected" ? "clause" : "validation"),
                    headline: source.headline || "Étape de comparaison",
                    message: source.message || "Traitement de la comparaison en cours.",
                });
            };

            const normalizeComparisonReasoningDeltaAsObservation = (payload) => {
                const source = payload?.data && typeof payload.data === "object"
                    ? payload.data
                    : payload || {};
                const message = typeof source.message === "string" ? source.message.trim() : "";
                if (!message) return null;

                const observationId = source.observation_id
                    || source.insight_id
                    || source.event_id
                    || source.candidate_id
                    || source.analysis_id
                    || `comparison-progress-${++comparisonReasoningObservationIndex}`;

                return canonicalFastGlobalEvent({
                    ...source,
                    event: "analysis_observation",
                    review_mode: source.review_mode || "comparison",
                    provisional: source.provisional !== undefined && source.provisional !== null
                        ? source.provisional
                        : true,
                    observation_id: `comparison-reasoning-${observationId}`,
                    stage: source.stage || "validation",
                    kind: source.kind || "validation",
                    headline: source.headline || "Progression de la comparaison",
                    message,
                });
            };

            const closeEventSourceSafely = (eventSource) => {
                if (isStreamClosed) return;
                isStreamClosed = true;
                if (eventSource?.close) eventSource.close();
            };

            const finalizeStreamOnce = (eventSource, reason, options = {}) => {
                if (streamFinalizePromise) return streamFinalizePromise;

                const {
                    shouldPersist = true,
                    ok = true,
                    markError = false,
                    errorPayload = null,
                    errorMessage = null,
                    finalizeReason: finalizeReasonOverride = null,
                    analysisStatus = ok ? "completed" : "failed",
                    finalizeComments = null,
                    preserveStructuredProgress = false,
                    resetSelection = true,
                } = options;

                const finalizeReason =
                    finalizeReasonOverride ||
                    (reason === "analysis_done"
                        ? "fallback_analysis_done"
                        : "terminal_event_received");

                streamTerminalState = { reason, ok, markError, finalizeReason };
                isFinalizingStream = true;
                closeProviderSummary(ok ? 'completed' : 'failed');
                if (preserveStructuredProgress) stopFastGlobalObservationReveal();
                else clearFastGlobalObservations();
                if (!ok || markError) removeTransientFastGlobalCards();

                streamFinalizePromise = (async () => {
                    closeEventSourceSafely(eventSource);
                    // A server-finalized terminal already confirms persistence.
                    // Refreshing cards or optional labels must not keep the UI running.
                    if (!shouldPersist) {
                        dispatch(setStreamingIa(false));
                        dispatch(setLoadingIAComments(false));
                        dispatch(finishAiTask(analysisTaskId));
                    }

                    try {
                        const resolvedDocumentVersionId = targetDocumentVersionId || id_doc;

                        if (markError) {
                            if (errorPayload) {
                                dispatch({
                                    type: IAStreamActionTypes.FINALIZE_ERROR,
                                    payload: errorPayload,
                                });
                            }

                            if (errorMessage) {
                                dispatch(setStageError(errorMessage));
                            }
                        }

                        if (shouldPersist) {
                            const commentsToFinalize = Array.isArray(finalizeComments)
                                ? finalizeComments
                                : streamedComments;
                            await finalizeAfterStream(
                                analyseId,
                                resolvedDocumentVersionId,
                                commentsToFinalize,
                                finalizeReason,
                                {
                                    reservationId: activeReservationId,
                                    analysis_status: analysisStatus,
                                    finalized_at: new Date().toISOString(),
                                }
                            );
                        }
                        refreshCommercialUsageBestEffort(dispatch);

                        if (!ok || markError) return;

                        const res = await getIAReviewResults(analyseId, resolvedDocumentVersionId);
                        dispatch(setIAComments(res.data));
                        transientFastGlobalCardIds.clear();
                        try {
                            const refreshedAnalyse = await getAnalyseFull(analyseId);
                            dispatch({
                                type: StageActionTypes.SET_LABELS_VARIANT,
                                payload: refreshedAnalyse?.data?.labels_variant,
                            });
                        } catch (metadataError) {
                            console.warn("Unable to refresh analysis labels variant after stream", metadataError);
                        }

                        dispatch(
                            setCriterionStates(selectedCriteria, {
                                status: analysisStatus,
                                executed_at: new Date().toISOString(),
                            })
                        );
                    } catch (e) {
                        closeProviderSummary('failed');
                        refreshCommercialUsageBestEffort(dispatch);
                        if (directStreamedCardIds.size > 0) {
                            dispatch({
                                type: IAStreamActionTypes.SET_ANALYSIS_REASONING_STATUS,
                                payload: 'failed',
                            });
                        }
                        dispatch({
                            type: IAStreamActionTypes.FINALIZE_ERROR,
                            payload: e,
                        });

                        dispatch(
                            setStageError(
                                e.response?.data?.error ||
                                e.response?.data?.message ||
                                e.response?.data?.detail ||
                                i18n.t("review:stage.server")
                            )
                        );
                    } finally {
                        isFinalizingStream = false;
                        dispatch(setStreamingIa(false));
                        dispatch(finishAiTask(analysisTaskId));
                        dispatch(setLoadingIAComments(false));
                        if (resetSelection) dispatch(initialiseAnalysis());
                        if (activeReservationId) {
                            dispatch(clearActiveReservationId());
                        }
                    }
                })();

                return streamFinalizePromise;
            };

            dispatch(setLoadingIAComments(true));
            dispatch(setStreamingIa(true));
            dispatch(startAiTask({
                ...aiTask.analysis(analyseId, targetDocumentVersionId || id_doc),
                id: analysisTaskId,
            }));
            dispatch({ type: IAStreamActionTypes.RESET_ANALYSIS_REASONING });
            dispatch({ type: IAStreamActionTypes.RESET_FAST_GLOBAL_STREAM });
            resetProviderSummary();
            dispatch({
                type: IAStreamActionTypes.RESET_REFERENCE_REVIEW,
                payload: {
                    analysis_id: analyseId,
                    document_version_id: targetDocumentVersionId || id_doc,
                    review_mode: analysisStreamMode,
                    stream_instance_id: referenceReviewStreamInstanceId,
                },
            });
            if (['fast_global', 'comparison', 'reference_review'].includes(analysisStreamMode)) {
                dispatch({
                    type: IAStreamActionTypes.SET_ANALYSIS_STREAM_MODE,
                    payload: analysisStreamMode,
                });
            }
            dispatch({ type: IAStreamActionTypes.SET_ANALYSIS_REASONING_STATUS, payload: "active" });

            const appendComparisonFailureObservation = () => {
                stopFastGlobalObservationReveal();
                dispatch({ type: IAStreamActionTypes.DISCARD_COMPARISON_PREVIEWS });
                dispatch({
                    type: IAStreamActionTypes.DISCARD_COMPARISON_CARD_PREVIEWS,
                    payload: { resetCardsStarted: true },
                });
                dispatch({
                    type: IAStreamActionTypes.ADD_FAST_GLOBAL_OBSERVATION,
                    payload: {
                        event: 'analysis_observation',
                        schema_version: 'comparison_stream_event_v1',
                        review_mode: 'comparison',
                        observation_id: `comparison-failed-${analyseId}`,
                        provisional: true,
                        stage: 'validation',
                        kind: 'validation',
                        headline: i18n.t('review:sidebar.fastGlobalTimeline.comparisonFailedHeadline'),
                        message: i18n.t('review:sidebar.fastGlobalTimeline.comparisonFailedMessage'),
                    },
                });
            };

            const failStream = (errorMessage, eventSource, extraPayload = null, kind = "transport_error") => {
                if (streamFailedOnce || streamTerminalState?.ok || isFinalizingStream || streamFinalizePromise) return;
                streamFailedOnce = true;
                const preserveComparisonProgress = analysisStreamMode === 'comparison';
                if (preserveComparisonProgress) {
                    appendComparisonFailureObservation();
                } else {
                    clearFastGlobalObservations();
                }
                dispatch({ type: IAStreamActionTypes.SET_ANALYSIS_REASONING_STATUS, payload: "failed" });
                interruptReferenceReview(kind || 'transport_error');

                // Auto-capture technique : best-effort, ne bloque jamais le flux d'analyse.
                reportTechnicalAutoFeedback({
                    errorKind: kind,
                    analyseId,
                    documentVersionId: targetDocumentVersionId || id_doc,
                    streamPhase: "analysis_stream",
                    errorMessage,
                });

                finalizeStreamOnce(eventSource, "transport_error", {
                    ok: false,
                    shouldPersist: false,
                    markError: true,
                    errorPayload: extraPayload || errorMessage,
                    errorMessage: errorMessage || i18n.t("review:stage.server"),
                    preserveStructuredProgress: preserveComparisonProgress,
                    resetSelection: false,
                });
            };

            try {
                const startStream = launchOptions.streamFactory || launchAnalysisStream;
                const eventSource = startStream(
                    analyseId,
                    selectedCriteria,
                    id_doc,
                    (msg) => {
                        if (!msg || isStreamClosed) return;
                        if (!analysisStreamMode && isFastGlobalStreamEvent(msg)) detectFastGlobalMode(msg);
                        // A late callback from an older launch must not start cards
                        // or terminate activity belonging to the current launch.
                        const activeActivity = getState?.()?.documentVersion?.provider_summary;
                        if (analysisStreamMode === 'fast_global' && activeActivity?.stream_instance_id
                            && activeActivity.stream_instance_id !== referenceReviewStreamInstanceId) return;
                        if (analysisStreamMode === 'fast_global' && msg.analysis_id
                            && msg.analysis_id !== analyseId) return;
                        if (analysisStreamMode === 'fast_global' && activeBatchId
                            && msg.batch_id && msg.batch_id !== activeBatchId) return;
                        if (analysisStreamMode === 'fast_global' && msg.schema_version === FAST_GLOBAL_BATCH_EVENT_SCHEMA
                            && typeof msg.batch_id === 'string' && msg.batch_id) {
                            if (activeBatchId && activeBatchId !== msg.batch_id) return;
                            activeBatchId = msg.batch_id;
                            if (['analysis_started', 'analysis_mechanism_progress', 'criterion_started',
                                'criterion_cache_hit', 'criterion_completed', 'criterion_failed', 'criterion_error',
                                'cross_criterion_consolidation_started'].includes(msg.event)) updateBatchActivity(msg);
                        }
                        if (msg.event === 'reference_requirement_progress') {
                            analysisStreamMode = 'reference_review';
                            dispatch({ type: IAStreamActionTypes.SET_ANALYSIS_STREAM_MODE, payload: 'reference_review' });
                            dispatchReferenceReviewProgress(
                                IAStreamActionTypes.UPSERT_REFERENCE_REQUIREMENT,
                                msg,
                            );
                            dispatch(setLoadingIAComments(false));
                            return;
                        }
                        if (msg.event === 'reference_verdict_progress') {
                            analysisStreamMode = 'reference_review';
                            dispatch({ type: IAStreamActionTypes.SET_ANALYSIS_STREAM_MODE, payload: 'reference_review' });
                            dispatchReferenceReviewProgress(
                                IAStreamActionTypes.UPSERT_REFERENCE_VERDICT,
                                msg,
                            );
                            dispatch(setLoadingIAComments(false));
                            return;
                        }
                        if (isComparisonEventType(msg?.event)) {
                            detectFastGlobalMode(msg);
                        }

                        if (msg.event === 'analysis_context') {
                            if (isUuid(msg.document_version_id)) {
                                targetDocumentVersionId = msg.document_version_id;
                                dispatch({
                                    type: DocumentActionTypes.SET_CURRENT_VERSION,
                                    payload: msg.document_version_id,
                                });
                            }
                            detectFastGlobalMode(msg);
                            if (analysisStreamMode === 'reference_review') {
                                activateReferenceReviewContext(msg);
                            }
                            if (msg.reservation_id) {
                                activeReservationId = String(msg.reservation_id);
                                dispatch(setActiveReservationId(activeReservationId));
                            }
                            return;
                        }

                        if (msg.event === 'analysis_started') {
                            detectFastGlobalMode(msg);
                            if (analysisStreamMode === 'fast_global') {
                                if (typeof msg.criterion_id === 'string' && msg.criterion_id
                                    && msg.analysis_id === analyseId) {
                                    const activeSummary = getState?.()?.documentVersion?.provider_summary;
                                    if (startedProviderSummaryCriteria.has(msg.criterion_id)
                                        && !fastGlobalCardsStarted && pendingFastGlobalCards.length === 0
                                        && activeSummary?.status === 'active'
                                        && activeSummary.analysis_id === analyseId
                                        && activeSummary.stream_instance_id === referenceReviewStreamInstanceId) {
                                        resetProviderSummary();
                                    }
                                    startedProviderSummaryCriteria.add(msg.criterion_id);
                                }
                                dispatch({ type: IAStreamActionTypes.START_FAST_GLOBAL_ANALYSIS });
                            }
                            dispatch(setLoadingIAComments(true));
                            return;
                        }

                        if (msg.event === 'analysis_observation') {
                            if (
                                analysisStreamMode === 'comparison'
                                && msg.comparison_state === 'assessment_preview'
                            ) {
                                const previewCard = msg?.card && typeof msg.card === 'object'
                                    ? adaptFastGlobalCommentCard(msg.card)
                                    : null;
                                if (previewCard?.id) {
                                    startFastGlobalCards();
                                    comparisonPreviewCardIds.add(String(previewCard.id));
                                    commitFastGlobalCard({
                                        ...previewCard,
                                        comparison_provisional: true,
                                    }, { persist: false });
                                }
                                return;
                            }
                            if (acceptFastGlobalEvent(msg) && !fastGlobalCardsStarted) {
                                const observation = canonicalFastGlobalEvent(msg);
                                enqueueFastGlobalObservation(observation, {
                                    upsert: analysisStreamMode === 'comparison'
                                        && observation.comparison_state === 'assessment_preview',
                                });
                            }
                            return;
                        }

                        if (msg.event === "comparison_progress" || msg.event === "comparison_candidate_detected") {
                            if (acceptFastGlobalEvent(msg) && !fastGlobalCardsStarted) {
                                const observation = normalizeComparisonAsObservation(msg);
                                enqueueFastGlobalObservation(observation);
                            }
                            return;
                        }

                        if (msg.event === "comparison_card") {
                            const cardPayload = msg?.card && typeof msg.card === "object"
                                ? msg.card
                                : msg;
                            msg = {
                                ...msg,
                                ...cardPayload,
                                event: "comment_card",
                            };
                        }

                        if (msg.event === "comparison_terminal") {
                            if (msg.event) {
                                msg = {
                                    ...msg,
                                    event: "analysis_done",
                                    completion_status: msg?.status || msg?.data?.status || "failed",
                                    data: {
                                        ...msg?.data,
                                        ok: (msg?.status || msg?.data?.status) === "completed"
                                            || (msg?.status || msg?.data?.status) === "partial",
                                    },
                                    review_mode: msg?.review_mode || "comparison",
                                    code: msg?.error_code || msg?.data?.error_code,
                                    message: msg?.message || msg?.data?.message,
                                };
                                msg.status = msg?.status || msg?.data?.status || "failed";
                            }
                        }

                        if (msg.event === 'analysis_provider_summary_delta') return;

                        if (msg.event === 'analysis_activity_note') {
                            if (analysisStreamMode === 'fast_global') {
                                dispatch({
                                    type: IAStreamActionTypes.APPEND_PROVIDER_SUMMARY,
                                    payload: { ...msg, stream_instance_id: referenceReviewStreamInstanceId },
                                });
                            }
                            return;
                        }

                        if (msg.event === 'analysis_thinking') {
                            if (acceptFastGlobalEvent(msg) && !fastGlobalCardsStarted) {
                                dispatch({
                                    type: IAStreamActionTypes.UPSERT_FAST_GLOBAL_THINKING,
                                    payload: canonicalFastGlobalEvent(msg),
                                });
                            }
                            return;
                        }

                        if (msg.event === "commercial_refusal") {
                            closeProviderSummary('failed');
                            streamFailedOnce = true;
                            streamTerminalState = {
                                reason: "commercial_refusal",
                                ok: false,
                                markError: false,
                            };
                            clearFastGlobalObservations();
                            dispatch({
                                type: IAStreamActionTypes.SET_ANALYSIS_REASONING_STATUS,
                                payload: "failed",
                            });
                            closeEventSourceSafely(eventSource);
                            dispatch(setStreamingIa(false));
                            dispatch(finishAiTask(analysisTaskId));
                            dispatch(setLoadingIAComments(false));
                            if (typeof launchOptions.onCommercialLaunchFailure === "function") {
                                launchOptions.onCommercialLaunchFailure(msg.decision || msg.data || msg);
                            }
                            return;
                        }

                        if (msg.event === "analysis_reasoning_delta") {
                            if (msg.is_public !== false) {
                                detectFastGlobalMode(msg);
                                if (analysisStreamMode === 'comparison' && acceptFastGlobalEvent(msg) && !fastGlobalCardsStarted) {
                                    const reasoningObservation = normalizeComparisonReasoningDeltaAsObservation(msg);
                                    if (reasoningObservation) {
                                        enqueueFastGlobalObservation(reasoningObservation);
                                        return;
                                    }
                                }
                                if (analysisStreamMode === 'fast_global') return;
                                dispatch({
                                    type: IAStreamActionTypes.ADD_ANALYSIS_REASONING,
                                    payload: {
                                        message: msg.message,
                                        display_language: msg.display_language,
                                    },
                                });
                            }
                            return;
                        }

                        if (msg.event === 'criterion_queued') {
                            detectFastGlobalMode(msg);
                            dispatch({
                                type: IAStreamActionTypes.SET_FAST_GLOBAL_CRITERION_STATUS,
                                payload: { ...msg, status: 'pending' },
                            });
                            return;
                        }

                        if (msg.event === 'criterion_started') {
                            detectFastGlobalMode(msg);
                            dispatch({
                                type: IAStreamActionTypes.SET_FAST_GLOBAL_CRITERION_STATUS,
                                payload: { ...msg, status: 'in_progress' },
                            });
                            return;
                        }

                        if (msg.event === 'criterion_cache_status') {
                            detectFastGlobalMode(msg);
                            dispatch({
                                type: IAStreamActionTypes.SET_FAST_GLOBAL_CRITERION_STATUS,
                                payload: msg,
                            });
                            return;
                        }

                        if (msg.event === 'criterion_cache_hit') {
                            detectFastGlobalMode(msg);
                            dispatch({
                                type: IAStreamActionTypes.SET_FAST_GLOBAL_CRITERION_STATUS,
                                payload: { ...msg, status: 'cache_hit' },
                            });
                            return;
                        }

                        if (msg.event === 'criterion_error') {
                            detectFastGlobalMode(msg);
                            dispatch({
                                type: IAStreamActionTypes.SET_FAST_GLOBAL_CRITERION_STATUS,
                                payload: { ...msg, status: 'failed' },
                            });
                            return;
                        }

                        if (msg.event === 'criterion_done') {
                            detectFastGlobalMode(msg);
                            dispatch({
                                type: IAStreamActionTypes.SET_FAST_GLOBAL_CRITERION_STATUS,
                                payload: { ...msg, status: msg.completion_status || 'failed' },
                            });
                            return;
                        }

                        if (msg.event === 'criterion_completed') {
                            detectFastGlobalMode(msg);
                            dispatch({
                                type: IAStreamActionTypes.SET_FAST_GLOBAL_CRITERION_STATUS,
                                payload: { ...msg, status: msg.completion_status || 'completed' },
                            });
                            return;
                        }

                        if (msg.event === 'criterion_failed') {
                            detectFastGlobalMode(msg);
                            dispatch({
                                type: IAStreamActionTypes.SET_FAST_GLOBAL_CRITERION_STATUS,
                                payload: { ...msg, status: 'failed' },
                            });
                            return;
                        }

                        if (msg.event === "error" || msg.type === "error") {
                            detectFastGlobalMode(msg);
                            if (analysisStreamMode === 'fast_global' && msg.criterion_id) {
                                dispatch({
                                    type: IAStreamActionTypes.SET_FAST_GLOBAL_CRITERION_STATUS,
                                    payload: { ...msg, status: 'failed' },
                                });
                                if (!activeBatchId) closeProviderSummary('failed');
                                return;
                            }
                            closeProviderSummary('failed');
                            clearFastGlobalObservations();
                            failStream(
                                msg.data?.message ||
                                msg.data ||
                                msg.message ||
                                i18n.t("review:stage.server"),
                                eventSource,
                                msg.data || msg
                            );
                            return;
                        }

                        if (msg.event === "routing_item") {
                            return;
                        }

                        if (msg.type === "system") {
                            if (msg.message === "analysis_context") {
                                if (isUuid(msg.document_version_id)) {
                                    targetDocumentVersionId = msg.document_version_id;
                                    dispatch({
                                        type: DocumentActionTypes.SET_CURRENT_VERSION,
                                        payload: msg.document_version_id,
                                    });
                                }
                                if (['fast_global', 'comparison', 'reference_review'].includes(msg.review_mode)) {
                                    analysisStreamMode = msg.review_mode;
                                    dispatch({
                                        type: IAStreamActionTypes.SET_ANALYSIS_STREAM_MODE,
                                        payload: msg.review_mode,
                                    });
                                }
                                if (analysisStreamMode === 'reference_review') {
                                    activateReferenceReviewContext(msg);
                                }
                                if (msg.reservation_id) {
                                    activeReservationId = String(msg.reservation_id);
                                    dispatch(setActiveReservationId(activeReservationId));
                                }
                                return;
                            }

                            if (msg.message === "analysis_started") {
                                if (analysisStreamMode === 'fast_global') {
                                    dispatch({ type: IAStreamActionTypes.START_FAST_GLOBAL_ANALYSIS });
                                }
                                dispatch(setLoadingIAComments(true));
                                return;
                            }

                            if (msg.message === "analysis_complete") {
                                closeProviderSummary('completed');
                                dispatch(setLoadingIAComments(false));
                                dispatch({
                                    type: IAStreamActionTypes.SET_ANALYSIS_REASONING_STATUS,
                                    payload: "completed",
                                });
                                return;
                            }

                            if (msg.message === "batch_heartbeat") {
                                console.info("analysis_stream_heartbeat", msg);
                                return;
                            }

                            return;
                        }

                        if (msg.event === "analysis_done") {
                            detectFastGlobalMode(msg);
                            // Direct risks are readable while streaming, but their priority
                            // is only settled by the terminal snapshots from the same call.
                            const terminalCards = msg.result?.cards || msg.data?.result?.cards;
                            if (analysisStreamMode === 'fast_global' && readAnalysisTerminal(msg).successful
                                && Array.isArray(terminalCards) && terminalCards.length) {
                                const directDrafts = [...streamedCommentDrafts.values()].filter(isDirectGlobalCard);
                                const draftIds = new Set(directDrafts.map((card) => String(card.id)));
                                const finalIds = terminalCards.map((card) => String(card.card_id || card.id || ''));
                                const priorities = terminalCards.map(getGlobalCardPriority);
                                const validOrder = draftIds.size > 0 && draftIds.size === terminalCards.length
                                    && new Set(finalIds).size === terminalCards.length
                                    && finalIds.every((id) => draftIds.has(id))
                                    && priorities.every(({ status, rank, reason }) => status === 'final' && rank && reason)
                                    && priorities.map(({ rank }) => rank).sort((a, b) => a - b)
                                        .every((rank, index) => rank === index + 1);
                                if (validOrder) {
                                    // Replace provisional persistence drafts, retaining their
                                    // criterion/citation context and their stable card identity.
                                    const otherComments = streamedComments.filter((card) => !draftIds.has(String(card.id)));
                                    streamedComments.splice(0, streamedComments.length, ...otherComments);
                                    terminalCards.forEach((snapshot) => {
                                        const id = String(snapshot.card_id || snapshot.id);
                                        const previous = streamedCommentDrafts.get(id) || {};
                                        commitFastGlobalCard(adaptFastGlobalCommentCard({
                                            ...previous, ...snapshot, id,
                                            event: 'comment_card', schema_version: FAST_GLOBAL_EVENT_SCHEMA,
                                            risk: snapshot.risk || snapshot.risk_level,
                                            metadata_json: { ...previous.metadata_json, ...snapshot.metadata_json },
                                        }));
                                    });
                                }
                            }
                            if (analysisStreamMode === 'fast_global' && activeBatchId
                                && Array.isArray(msg.result?.discarded_preview_ids)) {
                                const discarded = msg.result.discarded_preview_ids
                                    .filter(id => typeof id === 'string' && transientFastGlobalCardIds.has(id));
                                if (discarded.length) dispatch({ type: IAStreamActionTypes.REMOVE_IA_COMMENTS_BY_IDS, payload: discarded });
                            }
                            if (analysisStreamMode === 'fast_global') {
                                dispatch({
                                    type: IAStreamActionTypes.SET_FAST_GLOBAL_TERMINAL,
                                    payload: msg,
                                });
                            }
                            if (analysisStreamMode === 'reference_review') {
                                dispatch({
                                    type: IAStreamActionTypes.SET_REFERENCE_REVIEW_TERMINAL,
                                    payload: withActiveReferenceReviewIdentity(msg),
                                });
                            }
                            const terminal = readAnalysisTerminal(msg);
                            const ok = terminal.successful;
                            if (
                                analysisStreamMode === 'comparison'
                                && ok
                                && comparisonPreviewCardIds.size > 0
                            ) {
                                dispatch({
                                    type: IAStreamActionTypes.DISCARD_COMPARISON_CARD_PREVIEWS,
                                });
                                comparisonPreviewCardIds.clear();
                            }
                            const serverFinalized = msg?.server_finalized === true
                                || msg?.data?.server_finalized === true
                                || (analysisStreamMode === 'reference_review' && msg?.snapshot_persisted === true);

                            const partialNoPublishableCards = terminal.outcomeCode
                                === 'partial_no_publishable_cards';
                            if (partialNoPublishableCards && terminal.businessPartial) {
                                clearFastGlobalObservations();
                                dispatch({
                                    type: IAStreamActionTypes.SET_ANALYSIS_REASONING_STATUS,
                                    payload: "partial",
                                });
                                finalizeStreamOnce(eventSource, "analysis_done", {
                                    ok: false,
                                    shouldPersist: false,
                                    markError: false,
                                    finalizeReason: terminal.outcomeCode,
                                    analysisStatus: "partial",
                                    finalizeComments: [],
                                    preserveStructuredProgress: false,
                                });
                            } else if (ok) {
                                const analysisStatus = terminal.completionStatus === 'partial'
                                    ? 'partial'
                                    : 'completed';
                                const completeSuccessfulStream = () => {
                                    dispatch({
                                        type: IAStreamActionTypes.SET_ANALYSIS_REASONING_STATUS,
                                        payload: analysisStatus,
                                    });
                                    finalizeStreamOnce(eventSource, "analysis_done", {
                                        ok: true,
                                        shouldPersist: !serverFinalized,
                                        analysisStatus,
                                    });
                                };
                                if (['fast_global', 'comparison'].includes(analysisStreamMode) && !fastGlobalCardsStarted) {
                                    pendingFastGlobalCompletion = completeSuccessfulStream;
                                    finishFastGlobalReveal();
                                } else {
                                    completeSuccessfulStream();
                                }
                            } else {
                                const preserveStructuredProgress = analysisStreamMode === 'comparison';
                                if (analysisStreamMode === 'comparison') appendComparisonFailureObservation();
                                else if (!preserveStructuredProgress) clearFastGlobalObservations();
                                dispatch({
                                    type: IAStreamActionTypes.SET_ANALYSIS_REASONING_STATUS,
                                    payload: "failed",
                                });
                                const failedReason = terminal.outcomeCode
                                    || msg?.code
                                    || msg?.data?.reason
                                    || "analysis_failed";
                                const failedMessage = msg?.message || msg?.data?.message || i18n.t("review:stage.server");
                                reportTechnicalAutoFeedback({
                                    errorKind: failedReason,
                                    analyseId,
                                    documentVersionId: targetDocumentVersionId || id_doc,
                                    streamPhase: "analysis_stream",
                                    errorMessage: failedMessage,
                                });
                                finalizeStreamOnce(eventSource, "analysis_done", {
                                    ok: false,
                                    shouldPersist: true,
                                    markError: true,
                                    errorPayload: msg.data || msg,
                                    errorMessage: failedMessage,
                                    finalizeReason: failedReason,
                                    analysisStatus: "failed",
                                    finalizeComments: [],
                                    preserveStructuredProgress,
                                });
                            }
                            return;
                        }

                        const isStreamingCardEvent =
                            msg.event === "comment_card" ||
                            msg.event === "card_preview" ||
                            msg.event === "card_enrichment";

                        const rawComment =
                            (msg.type === "comment" && msg.data)
                                ? msg.data
                                : isStreamingCardEvent
                                    ? msg
                                : (msg.id && msg.title)
                                    ? msg
                                    : null;
                        const isBatchPreview = msg.event === 'card_preview' && activeBatchId
                            && msg.batch_id === activeBatchId && analysisStreamMode === 'fast_global';
                        const comment = adaptFastGlobalCommentCard(isBatchPreview
                            ? { ...rawComment, event: 'comment_card' } : rawComment);

                        if (comment) {
                            const eventType = msg.event || comment.event;
                            if (isBatchPreview) {
                                comment.event = 'card_preview';
                                startFastGlobalCards();
                                transientFastGlobalCardIds.add(String(comment.id));
                                commitFastGlobalCard(comment, { persist: false });
                                return;
                            }
                            if (
                                analysisStreamMode === 'comparison'
                                && eventType === 'comment_card'
                                && comment.id
                            ) {
                                comment.comparison_provisional = false;
                                comparisonPreviewCardIds.delete(String(comment.id));
                            }
                            if (eventType === 'comment_card' && acceptFastGlobalEvent(msg)) {
                                if (activeBatchId) comment.skip_save = false;
                                if (!fastGlobalCardsStarted) {
                                    pendingFastGlobalCards.push(comment);
                                    finishFastGlobalReveal();
                                    return;
                                }
                                commitFastGlobalCard(comment);
                                return;
                            }
                            dispatch(setLoadingIAComments(false));

                            if (comment.system_notice && comment.title?.includes("[INFO:CONTRACT_TRUNCATED]")) {
                                Swal.fire({
                                    title: i18n.t("review:stage.partialContractTitle"),
                                    html: (comment.description || "").replace(/\n/g, "<br>"),
                                    icon: "info",
                                    confirmButtonText: "OK",
                                });
                                return;
                            }

                            dispatch({ type: IAStreamActionTypes.ADD_IA_COMMENT, payload: comment });

                            if (!comment.error) {
                                const commentId = comment.id ? String(comment.id) : "";
                                const previousDraft = commentId
                                    ? (streamedCommentDrafts.get(commentId) || {})
                                    : {};
                                const mergedDraft = { ...previousDraft, ...comment };

                                if (eventType === "card_enrichment") {
                                    delete mergedDraft.skip_save;
                                }

                                if (commentId) {
                                    streamedCommentDrafts.set(commentId, mergedDraft);
                                }

                                if (!comment.skip_save) {
                                    streamedComments.push(mergedDraft);
                                }
                            }

                            return;
                        }

                        if (msg.type === "end") {
                            return;
                        }

                        if (msg.event === "warning") {
                            console.warn("analysis_stream_warning", msg.data || msg);
                        }
                    },
                    (transportError) => {
                        if (streamTerminalState?.ok || isFinalizingStream) {
                            closeEventSourceSafely(eventSource);
                            return;
                        }
                        failStream(
                            transportError?.message || i18n.t("review:stage.server"),
                            eventSource,
                            transportError
                        );
                    },
                    launchOptions
                );

                return () => {
                    if (!streamTerminalState) closeProviderSummary('failed');
                    if (!streamTerminalState?.ok) removeTransientFastGlobalCards();
                    if (!streamTerminalState) interruptReferenceReview('cancelled');
                    clearFastGlobalObservations();
                    closeEventSourceSafely(eventSource);
                };
            } catch (error) {
                closeProviderSummary('failed');
                if (
                    typeof launchOptions.onCommercialLaunchFailure === "function" &&
                    shouldSurfaceCommercialLaunchFailure(error)
                ) {
                    launchOptions.onCommercialLaunchFailure(error);
                }
                dispatch(setStreamingIa(false));
                dispatch(finishAiTask(analysisTaskId));
                dispatch(setLoadingIAComments(false));
                dispatch({
                    type: IAStreamActionTypes.SET_ANALYSIS_REASONING_STATUS,
                    payload: "failed",
                });
                interruptReferenceReview('launch_failed');
                dispatch(
                    setStageError(
                        error.response?.data?.error ||
                        error.response?.data?.message ||
                        error.response?.data?.detail ||
                        error.message ||
                        i18n.t("review:stage.server")
                    )
                );
                throw error;
            }
        };


export const fetchCriteriaForAnalysis = (analyseId, doc_id) => async (dispatch) => {
    try {
        const res = await get_criteria_for_analysis(analyseId, doc_id);
        dispatch({
            type: StageActionTypes.SET_CRITERIA_STAGE,
            payload: res.data
        });
    } catch (error) {
        dispatch(setStageError(
            error.response?.data?.error ||
            error.response?.data?.message ||
            error.response?.data?.detail ||
            i18n.t("review:stage.reloadCriteriaError")
        ));
    }
};

export const flushAnalyseCriteria = () => async (dispatch, getState) => {
    try {
        const analyseId = getState().stage?.id;
        const documentVersionState = getState().documentVersion || {};
        const stageState = getState().stage || {};
        const documentId =
            documentVersionState.currentVersion ||
            documentVersionState.content?.id ||
            stageState.versions?.[0]?.id;

        if (!analyseId || !documentId) {
            dispatch(setStageError(i18n.t("review:stage.flushError")));
            throw new Error("missing_analysis_or_document_version_id");
        }
        const res = await flushAnalyseVersion(analyseId, documentId);
        dispatch(flushAnalyse());
        dispatch(flushIaComments());
        dispatch({
            type: IAStreamActionTypes.SET_REFERENCE_REVIEW_UNAVAILABLE,
            payload: {
                analysis_id: analyseId,
                document_version_id: documentId,
                reason: "flush",
            },
        });
        dispatch(resetAnalysisReasoning());
        dispatch(initialiseAnalysis());

        // Réinitialiser la version courante côté UI/documentVersion
        // (SET_CURRENT_VERSION attend un payload qui est l'ID ou null)
        // Notifier les composants UI (ex: sidebar des commentaires) qu'une flush globale a eu lieu
        // afin qu'ils puissent vider leurs filtres locaux (ex: filtre par critère)
        try {
            window.dispatchEvent(new CustomEvent('analysisFlushed'));
        } catch (e) {
            // ignore si non supporté (tests, SSR, etc.)
        }

        return res.data;
    } catch (error) {
        dispatch(setStageError(
            error.response?.data?.error ||
            error.response?.data?.message ||
            error.response?.data?.detail ||
            error.message ||
            i18n.t("review:stage.flushError")
        ));
        throw error;
    }
};

export function RequestGenerateSummary(summaryRequest, onStreamUpdate, onDone,onError) {
    return async function (dispatch) {
        try {
            const res = await generate_summary_from_comments(summaryRequest);
            const reader = res.body.getReader();
            const decoder = new TextDecoder("utf-8");
            let fullSummary = "";

            while (true) {
                const {done, value} = await reader.read();
                if (done) break;
                const chunk = decoder.decode(value, {stream: true});
                fullSummary += chunk;
                onStreamUpdate(fullSummary);
            }

            fullSummary += decoder.decode();
            onDone && onDone(fullSummary);
        } catch (err) {
            onError && onError(err)

            dispatch(setCriterionError(i18n.t("review:stage.summaryError")));
            dispatch(setStageError(
                err.response?.data?.error ||
                err.response?.data?.message ||
                err.response?.data?.detail ||
                i18n.t("review:stage.summaryServer")
            ));
        }
    };
}

// Actions de base
export const selectTemplate = (template) => (dispatch) => ({
    type: StageActionTypes.SET_SELECTED_TEMPLATE,
    payload: template
});

export const flushAnalyse = () => ({type: StageActionTypes.FLUSH_ANALYSE});

export const toggleCriterionSelection = (criterionId) => ({
    type: StageActionTypes.TOGGLE_CRITERION_SELECTION,
    payload: criterionId
});

export const replaceCriteriaSelection = (criteriaIds) => ({
    type: StageActionTypes.REPLACE_CRITERIA_SELECTION,
    payload: criteriaIds
});

export const addAmendmentStage = (payload) => ({
    type: StageActionTypes.ADD_STAGE_AMENDMENT,
    payload: payload
});

export const setLoadingIAComments = (payload) => ({
    type: IAStreamActionTypes.SET_ID_LOADING_COMMENTS,
    payload: payload
});

export const flushIaComments = () => ({
    type: IAStreamActionTypes.FLUSH_IA_COMMENTS,
});

export const resetAnalysisReasoning = () => ({
    type: IAStreamActionTypes.RESET_ANALYSIS_REASONING,
});

export const initialiseAnalysis = () => ({
    type: StageActionTypes.INITIALISE_ANALYSIS,
});

export const addVersion = (versionObject, main_Doc) => ({
    type: StageActionTypes.ADD_VERSION,
    versionObject: versionObject,
    main_Doc: main_Doc
});

export const setIAComments = (payload) => ({
    type: IAStreamActionTypes.SET_IA_COMMENTS,
    payload: payload
});

export const pathIAComments = (payload) => ({
    type: IAStreamActionTypes.PATCH_IA_COMMENTS,
    payload: payload
});

export const setCriterionStates = (ids, updates) => ({
    type: StageActionTypes.SET_CRITERION_STATES,
    payload: {ids, updates},
});

export const setStageLabelsVariant = (labelsVariant) => ({
    type: StageActionTypes.SET_LABELS_VARIANT,
    payload: labelsVariant,
});

export const updateCriterionRefs = (id, refs) => ({
    type: StageActionTypes.UPDATE_CRITERION_REFS,
    payload: {id, refs},
});

export const addCriterionToList = (criterion) => ({
    type: StageActionTypes.ADD_CRITERION_TO_LIST,
    payload: criterion,
});

export const updateCriterionInList = (criterion) => ({
    type: StageActionTypes.UPDATE_CRITERION_IN_LIST,
    payload: criterion,
});

export const addReferenceFileToStage = (file) => ({
    type: StageActionTypes.ADD_REFERENCE_FILE_TO_STAGE,
    payload: file,
});

export const setStageError = (errorMessage) => ({
    type: StageActionTypes.SET_ERROR,
    payload: errorMessage,
});
